import classNames from "classnames";
import { ReactNode } from "react";
import { useDispatch } from "react-redux";
import ReactTooltip from "react-tooltip";

import { AnnotationType, ViewerMode } from "custom-models/ViewerTypes";

import {
  addAnnotation,
  changeViewerMode,
  resetNewPenDrawing,
} from "shared/actions/slideDetailActions";
import { AIState } from "shared/reducers/aiReducer";
import { AuthState } from "shared/reducers/authReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { notFound } from "shared/utils/annotations";

type AnnotationToolButtonProps = {
  wantedViewerMode: ViewerMode;
  slideDetailObject: SlideDetailState;
  aiObject?: AIState;
  authObject: AuthState;
  title: string;
  children: ReactNode;
  mppRequired?: boolean;
  maskRequired?: boolean;
};

const AnnotationToolButton = ({
  slideDetailObject,
  aiObject,
  wantedViewerMode,
  title,
  children,
  mppRequired,
  maskRequired,
}: AnnotationToolButtonProps) => {
  const { viewerMode, mpp } = slideDetailObject;
  const dispatch = useDispatch();

  const handleAnyNewPenDrawing = () => {
    const { newPenDrawing, pendingAnnotationsBuffer } = slideDetailObject;
    if (
      newPenDrawing &&
      slideDetailObject.id &&
      notFound(pendingAnnotationsBuffer, newPenDrawing)
    ) {
      if (
        newPenDrawing.type === AnnotationType.PEN_DRAWING &&
        newPenDrawing.pathInfo.indexOf("L") === -1
      ) {
        dispatch(resetNewPenDrawing());
      } else {
        dispatch(addAnnotation(newPenDrawing, slideDetailObject.id));
      }
    }
  };

  const mppUnavailable = mppRequired && !mpp;

  return (
    <>
      <button
        title={title}
        disabled={maskRequired && aiObject && !aiObject.maskLesionLineType}
        className={classNames(
          viewerMode === wantedViewerMode ? "active" : undefined,
          (mppUnavailable ||
            (maskRequired && aiObject && !aiObject.maskLesionLineType)) &&
            "unavailable"
        )}
        onClick={
          mppUnavailable
            ? undefined
            : () => {
                handleAnyNewPenDrawing();
                dispatch(changeViewerMode(wantedViewerMode));
              }
        }
        data-tip=""
        data-for={`button-${title}`}
      >
        {children}
      </button>
      <ReactTooltip
        place="bottom"
        id={`button-${title}`}
        type={mppUnavailable ? "error" : "info"}
      >
        {title}
        {mppUnavailable && " (MPP Not Available)"}
      </ReactTooltip>
    </>
  );
};

export default AnnotationToolButton;
